import React, { useState, useEffect } from "react";
import styles from "./Faq.module.css";
import { getFaqData, FAQData } from "../../lib/getFaq";

const Faq: React.FC = () => {
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [faqData, setFaqData] = useState<FAQData>({ categories: [] });
  const [filteredFaqs, setFilteredFaqs] = useState<FAQData["categories"]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch FAQ data on component mount
  useEffect(() => {
    const loadFaqData = async () => {
      try {
        setLoading(true);
        const data = await getFaqData();
        setFaqData(data);
        setFilteredFaqs(data.categories);
        setLoading(false);
      } catch (err) {
        setError("Failed to load FAQ data. Please try again later.");
        setLoading(false);
        console.error("Error loading FAQ data:", err);
      }
    };

    loadFaqData();
  }, []);

  // Handle search functionality
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredFaqs(faqData.categories);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();

    const filtered = faqData.categories
      .map((category) => {
        const matchedFaqs = category.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(lowercasedSearch) ||
            faq.answer.toLowerCase().includes(lowercasedSearch)
        );

        if (matchedFaqs.length > 0) {
          return {
            ...category,
            faqs: matchedFaqs,
          };
        }
        return null;
      })
      .filter(Boolean) as typeof faqData.categories;

    setFilteredFaqs(filtered);
  }, [searchTerm, faqData.categories]);

  const toggleQuestion = (id: number) => {
    setActiveQuestion(activeQuestion === id ? null : id);
  };

  if (loading) {
    return (
      <div className="container">
        <div className={styles.faqContainer}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Cargando preguntas frecuentes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className={styles.faqContainer}>
          <div className={styles.errorContainer}>
            <p className={styles.error}>{error}</p>
            <button
              className="buttonSecondary"
              onClick={() => window.location.reload()}
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className={styles.faqContainer}>
        <div className={styles.faqHeader}>
          <h1 className={styles.faqTitle}>Preguntas Frecuentes</h1>
          <p className={styles.faqSubtitle}>
            Encuentra respuestas a las preguntas más comunes sobre nuestros
            productos y servicios.
          </p>

          <div className={styles.faqSearch}>
            <input
              type="text"
              placeholder="Buscar preguntas..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className={styles.searchIcon}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                  stroke="var(--gray-300)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 21L16.65 16.65"
                  stroke="var(--gray-300)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {filteredFaqs.length > 0 ? (
          filteredFaqs.map((category) => (
            <div key={category.id} className={styles.faqSection}>
              <h2 className={styles.sectionTitle}>{category.name}</h2>

              {category.faqs.map((faq) => (
                <div key={faq.id} className={styles.faqItem}>
                  <div
                    className={`${styles.faqQuestion} ${
                      activeQuestion === faq.id ? styles.active : ""
                    }`}
                    onClick={() => toggleQuestion(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <div
                      className={`${styles.faqArrow} ${
                        activeQuestion === faq.id ? styles.open : ""
                      }`}
                    >
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1L7 7L13 1"
                          stroke="var(--blue-evefant-dark-3)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div
                    className={`${styles.faqAnswer} ${
                      activeQuestion === faq.id ? styles.open : ""
                    }`}
                  >
                    <div className={styles.answerContent}>{faq.answer}</div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className={styles.noResults}>
            <p>
              No se encontraron resultados para tu búsqueda. Intenta con otros
              términos.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Faq;
